import React, { useState } from 'react';
import usePageTranslation from '../../../../shared/hooks/usePageTranslation';

interface Ticket {
  id: number;
  subject: string;
  category: string;
  status: 'ouvert' | 'en_cours' | 'resolu' | 'ferme';
  created_at: string;
  last_update: string;
  messages_count: number;
}

interface SupportModalProps {
  isOpen: boolean;
  onClose: () => void;
}

const SupportModal: React.FC<SupportModalProps> = ({ isOpen, onClose }) => {
  const { t } = usePageTranslation('game', 'SupportModal');
  const [showCreateTicket, setShowCreateTicket] = useState(false);
  // TODO: Récupérer depuis l'API
  const [tickets] = useState<Ticket[]>([]);
  const [selectedTicket, setSelectedTicket] = useState<Ticket | null>(null);
  const [formData, setFormData] = useState({
    subject: '',
    category: 'technique',
    message: '',
  });

  const categories = [
    { value: 'technique', label: t('categories.technique') },
    { value: 'bug', label: t('categories.bug') },
    { value: 'question', label: t('categories.question') },
    { value: 'suggestion', label: t('categories.suggestion') },
    { value: 'autre', label: t('categories.autre') },
  ];

  const statusColors = {
    ouvert: 'bg-blue-500/30 text-blue-300 border-blue-500/50',
    en_cours: 'bg-yellow-500/30 text-yellow-300 border-yellow-500/50',
    resolu: 'bg-green-500/30 text-green-300 border-green-500/50',
    ferme: 'bg-gray-500/30 text-gray-300 border-gray-500/50',
  };

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    // TODO: Envoyer le ticket à l'API
    console.log('Création du ticket:', formData);
    // Réinitialiser le formulaire
    setFormData({ subject: '', category: 'technique', message: '' });
    setShowCreateTicket(false);
    // TODO: Rafraîchir la liste des tickets
  };

  if (!isOpen) return null;

  return (
    <>
      <div className="fixed inset-0 bg-black/50 z-50 flex items-center justify-center p-4" onClick={onClose}>
        <div
          className="bg-gray-900 rounded-lg shadow-2xl w-full max-w-4xl max-h-[90vh] overflow-hidden flex flex-col"
          onClick={(e) => e.stopPropagation()}
        >
          {/* Header */}
          <div className="flex items-center justify-between p-6 border-b border-gray-700">
            <h2 className="text-2xl font-bold text-white">{t('title')}</h2>
            <button
              onClick={onClose}
              className="text-gray-400 hover:text-white transition-colors"
            >
              <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
              </svg>
            </button>
          </div>

          {/* Content */}
          <div className="flex-1 overflow-y-auto p-6">
            <div className="flex items-center justify-between mb-6">
              <h3 className="text-xl font-semibold text-white">{t('myTickets')}</h3>
              <button
                onClick={() => setShowCreateTicket(!showCreateTicket)}
                className="px-4 py-2 bg-primary-600 hover:bg-primary-700 text-white rounded-lg transition-colors flex items-center space-x-2"
              >
                <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 4v16m8-8H4" />
                </svg>
                <span>{showCreateTicket ? t('cancelCreate') : t('createTicket')}</span>
              </button>
            </div>

            {showCreateTicket && (
              <div className="mb-6 p-4 bg-gray-800/50 rounded-lg border border-gray-700">
                <h4 className="text-lg font-semibold text-white mb-4">{t('createTicket')}</h4>
                <form onSubmit={handleSubmit} className="space-y-4">
                  <div>
                    <label className="block text-sm font-medium text-gray-300 mb-2">
                      {t('form.subject')}
                    </label>
                    <input
                      type="text"
                      value={formData.subject}
                      onChange={(e) => setFormData({ ...formData, subject: e.target.value })}
                      className="w-full px-4 py-2 bg-gray-700 border border-gray-600 rounded-lg text-white focus:outline-none focus:border-primary-500"
                      placeholder={t('form.subjectPlaceholder')}
                      required
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-300 mb-2">
                      {t('form.category')}
                    </label>
                    <select
                      value={formData.category}
                      onChange={(e) => setFormData({ ...formData, category: e.target.value })}
                      className="w-full px-4 py-2 bg-gray-700 border border-gray-600 rounded-lg text-white focus:outline-none focus:border-primary-500"
                    >
                      {categories.map((cat) => (
                        <option key={cat.value} value={cat.value}>
                          {cat.label}
                        </option>
                      ))}
                    </select>
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-300 mb-2">
                      {t('form.message')}
                    </label>
                    <textarea
                      value={formData.message}
                      onChange={(e) => setFormData({ ...formData, message: e.target.value })}
                      className="w-full px-4 py-2 bg-gray-700 border border-gray-600 rounded-lg text-white focus:outline-none focus:border-primary-500"
                      rows={4}
                      placeholder={t('form.messagePlaceholder')}
                      required
                    />
                  </div>
                  <div className="flex space-x-3">
                    <button
                      type="submit"
                      className="px-6 py-2 bg-primary-600 hover:bg-primary-700 text-white rounded-lg transition-colors"
                    >
                      {t('form.submit')}
                    </button>
                    <button
                      type="button"
                      onClick={() => {
                        setShowCreateTicket(false);
                        setFormData({ subject: '', category: 'technique', message: '' });
                      }}
                      className="px-6 py-2 bg-gray-700 hover:bg-gray-600 text-white rounded-lg transition-colors"
                    >
                      {t('form.cancel')}
                    </button>
                  </div>
                </form>
              </div>
            )}

            {tickets.length === 0 ? (
              <div className="text-center py-12 text-gray-400">
                <svg className="w-16 h-16 mx-auto mb-4 opacity-50" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
                </svg>
                <p className="text-lg mb-2">{t('noTickets')}</p>
                <p className="text-sm">{t('noTicketsDesc')}</p>
              </div>
            ) : (
              <div className="space-y-3">
                {tickets.map((ticket) => (
                  <div
                    key={ticket.id}
                    onClick={() => setSelectedTicket(ticket)}
                    className="p-4 bg-gray-800/50 hover:bg-gray-800/70 rounded-lg border border-gray-700 hover:border-primary-500/50 transition-all cursor-pointer"
                  >
                    <div className="flex items-center justify-between">
                      <div className="flex-1">
                        <div className="flex items-center space-x-3 mb-2">
                          <h5 className="text-white font-semibold">{ticket.subject}</h5>
                          <span className={`px-2 py-1 rounded text-xs font-medium border ${statusColors[ticket.status]}`}>
                            {t(`status.${ticket.status}`)}
                          </span>
                        </div>
                        <div className="flex items-center space-x-4 text-sm text-gray-400">
                          <span className="capitalize">{t(`categories.${ticket.category}`)}</span>
                          <span>•</span>
                          <span>{t('ticket.created')} {new Date(ticket.created_at).toLocaleDateString('fr-FR')}</span>
                          <span>•</span>
                          <span>{ticket.messages_count} {ticket.messages_count > 1 ? t('ticket.messagesPlural') : t('ticket.messages')}</span>
                        </div>
                      </div>
                      <svg className="w-5 h-5 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
                      </svg>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>
        </div>
      </div>

      {/* Modal de détails du ticket */}
      {selectedTicket && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-[60] p-4" onClick={() => setSelectedTicket(null)}>
          <div className="bg-gray-800 rounded-lg p-6 max-w-2xl w-full max-h-[80vh] overflow-y-auto" onClick={(e) => e.stopPropagation()}>
            <div className="flex items-center justify-between mb-4">
              <h4 className="text-xl font-bold text-white">{selectedTicket.subject}</h4>
              <button
                onClick={() => setSelectedTicket(null)}
                className="text-gray-400 hover:text-white"
              >
                <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                </svg>
              </button>
            </div>
            <div className="space-y-4">
              <div className="flex items-center space-x-4 text-sm text-gray-400">
                <span className={`px-3 py-1 rounded border ${statusColors[selectedTicket.status]}`}>
                  {t(`status.${selectedTicket.status}`)}
                </span>
                <span className="capitalize">{t(`categories.${selectedTicket.category}`)}</span>
                <span>•</span>
                <span>{t('ticket.created')} {new Date(selectedTicket.created_at).toLocaleDateString('fr-FR')}</span>
              </div>
              <div className="bg-gray-700/50 rounded-lg p-4">
                <p className="text-white">Détails du ticket...</p>
                {/* TODO: Afficher les messages du ticket depuis l'API */}
              </div>
            </div>
          </div>
        </div>
      )}
    </>
  );
};

export default SupportModal;

